import type { ChartBlock } from '../spec/types'

/**
 * Horizontal proportion bars — the only chart shape this console draws.
 *
 * Verbatim from `docs/design/reference/blocks/chart.html`. A three-track grid:
 * the label, a 9px rail with a toned fill, and the figure with its unit pinned
 * right on a 120px minimum so a column of figures reads as a column.
 *
 * There is deliberately no axis, no gridline and no time series. The corpus
 * uses this for six things — today's shape, recognition coverage by confidence
 * tier, outbox depth by destination, fee revenue by kind, the activation funnel
 * and deflection by intent — and every one of them is a set of parts an
 * operator compares against each other, not a trend to eyeball. A drawn axis
 * would invite reading a value off the bar; the number is printed instead,
 * because the bar is the comparison and the text is the figure.
 *
 * The value and the unit are separate spans, adjacent, and the unit is the ISO
 * code when the figure is money (invariant 4 — `198,412.50` next to `EUR`).
 * Both arrive pre-composed in the spec, so nothing here formats an amount.
 *
 * ONE DELIBERATE DEVIATION: the prototype's projector passes the percentage
 * through as a bare number (`width:78`), which every browser discards as an
 * invalid declaration — its own rendered output shows the fills with no width
 * at all. That is a bug in the projector, not a design that draws empty rails,
 * and the sibling `b` table cell proves the intent by emitting `arr[1] + '%'`
 * for the same tuple shape. The unit is restored here.
 */
export function Chart({ block }: { block: ChartBlock }) {
  return (
    <div style={{ border: '1px solid var(--k-line)', background: 'var(--k-panel)', padding: '12px' }}>
      {block.series.map((s, i) => (
        <div
          // Series are positional in the design corpus; the ordering is the
          // spec's own (match order, funnel order, depth) and carries no id.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(120px,220px) 1fr auto',
            alignItems: 'center',
            gap: '12px',
            padding: '4px 0',
            borderBottom: '1px solid var(--k-line)',
          }}
        >
          <span
            style={{
              fontSize: '12px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {s[0]}
          </span>
          <span style={{ height: '9px', background: 'var(--k-panel3)', position: 'relative' }}>
            <span
              data-bar={s[4]}
              style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${s[3]}%` }}
            />
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '4px',
              fontSize: '12px',
              fontWeight: 600,
              minWidth: '120px',
              justifyContent: 'flex-end',
            }}
          >
            <span>{s[1]}</span>
            <span
              style={{ fontFamily: 'var(--k-mono)', fontSize: '9.5px', color: 'var(--k-mut)' }}
            >
              {s[2]}
            </span>
          </span>
        </div>
      ))}
      <div style={{ fontSize: '11.5px', color: 'var(--k-mut)', marginTop: '9px' }}>
        {block.note2 ?? ''}
      </div>
    </div>
  )
}
